import type { ColumnHelp } from './components/HelpHeader.js';

/**
 * The explanations for the columns in the results table.
 *
 * Written for someone who is NOT from telecom: no undefined jargon, with the
 * emphasis on "what this means for me" rather than on the definition in the
 * standard.
 */
export const COLUMN_HELP: Record<string, ColumnHelp> = {
  msisdn: {
    title: 'The number dialled',
    body: 'The canonical form used for dialling: digits only, no "+" and no separators. '
        + 'This is exactly how we send it to the SIP trunk. If the uploaded file had it '
        + 'written with a "+" or with spaces, it was normalised at import.',
  },
  when: {
    title: 'When it was called',
    body: 'The time at which the call was placed. Useful when you re-run a batch: you can '
        + 'see at once which results come from the old run and which from the new one.',
  },
  verdict: {
    title: 'The verdict — the platform’s conclusion',
    body: 'What the network did, translated into ordinary language. It does not come from a '
        + 'single signal: what the switch reported, the codes received from the network and '
        + 'the shape of the recorded sound are combined. When the three disagree, the verdict '
        + 'is kept but the confidence drops.',
    values: [
      { key: 'ANSWERED', meaning: 'someone or something answered (human, machine, voicemail)' },
      { key: 'NO_ANSWER', meaning: 'it rang, nobody answered' },
      { key: 'PHONE_OFF', meaning: 'the phone is switched off or out of coverage' },
      { key: 'BUSY_OR_REJECTED', meaning: 'busy, or the call was rejected — the two cannot be separated' },
      { key: 'NOT_ALLOCATED', meaning: 'the number does not exist in the network' },
      { key: 'NETWORK_ERROR', meaning: 'a network error, not an answer about the subscriber' },
      { key: 'UNKNOWN', meaning: 'no classification rule matched' },
    ],
  },
  confidence: {
    title: 'Confidence in the verdict',
    body: 'From 0 to 1. Above 0.9 means every signal points the same way. Below 0.7 means they '
        + 'contradict each other — for example the codes say "phone off", but the recording '
        + 'contains a normal ringing tone. Rows with low confidence are worth listening to '
        + 'before they are taken at face value.',
  },
  dialstatus: {
    title: 'DIALSTATUS — what the switch reported',
    body: 'The raw result of the call attempt, as Asterisk sees it at the end. It is the first '
        + 'source the verdict is derived from, but not the only one.',
    values: [
      { key: 'ANSWER', meaning: 'the call was picked up' },
      { key: 'NOANSWER', meaning: 'it rang until the timeout expired' },
      { key: 'BUSY', meaning: 'busy or rejected' },
      { key: 'CHANUNAVAIL', meaning: 'the destination could not be reached' },
      { key: 'CONGESTION', meaning: 'network overloaded or a routing problem' },
    ],
  },
  sip: {
    title: 'The SIP code — the network’s response',
    body: 'The numeric code with which the network ended the call, from the SIP protocol. '
        + 'Hover over it to see the reason in text as well, exactly as the carrier sent it. '
        + 'CAREFUL: the code is not always honest — a switched-off phone can report '
        + '"no answer".',
    values: [
      { key: '200', meaning: 'answered' },
      { key: '404 / 604', meaning: 'the number does not exist' },
      { key: '486 / 600', meaning: 'busy' },
      { key: '480', meaning: 'temporarily unavailable — often a phone that is off' },
      { key: '603 / 403', meaning: 'call refused' },
      { key: '503 / 502', meaning: 'a problem inside the network' },
    ],
  },
  q850: {
    title: 'Q.850 — the cause from the telephony standard',
    body: 'The reason for the hangup, in the classic telephony encoding, older and finer '
        + 'grained than SIP. It often arrives together with the SIP code and helps with '
        + 'distinctions the latter does not make.',
    values: [
      { key: '16', meaning: 'normal clearing' },
      { key: '17', meaning: 'subscriber busy' },
      { key: '18 / 19', meaning: 'no answer' },
      { key: '1', meaning: 'unallocated number' },
      { key: '21', meaning: 'call rejected' },
      { key: '34 / 38', meaning: 'network unavailable' },
    ],
  },
  pdd: {
    title: 'PDD — the delay before the first sound',
    body: 'Post-Dial Delay: the time between sending the call and the network sending the '
        + 'first audio signal. In practice, how long you wait with the phone to your ear '
        + 'before you hear anything. Under 2 seconds is normal; over 5 seconds indicates '
        + 'poor routing, with too many hops before the destination.',
  },
  early: {
    title: 'Early media — sound before the answer',
    body: 'Whether the network sent audio BEFORE anyone picked up: the ringing tone, or an '
        + 'announcement of the kind "the number you have dialled is not available". This is '
        + 'where the truth lies when the codes lie — which is why the recording starts from '
        + 'early media, not from the answer.',
  },
  audio: {
    title: 'The shape of the recorded sound',
    body: 'Automatic analysis of the recording. The distinction matters: the ringing tone is '
        + 'intermittent, whereas a network announcement or a voice is continuous. That is how '
        + '"it rang and nobody answered" is separated from "the network explained that the '
        + 'call cannot go through".',
    values: [
      { key: 'intermittent', meaning: 'ringing tone — it is ringing at the destination' },
      { key: 'continuous', meaning: 'someone is speaking: a network announcement, a machine or a person' },
      { key: 'silent', meaning: 'it connected, but no sound arrives' },
      { key: 'empty', meaning: 'nothing was recorded — the network sent no audio' },
      { key: 'unclear', meaning: 'too little material to decide' },
    ],
  },
  billable: {
    title: 'The seconds paid for',
    body: 'The billed duration, measured from the moment the call was picked up until the '
        + 'hangup. Ringing is NOT billed, however long it lasts. That is why the cost of a '
        + 'campaign depends almost entirely on the length of the message played, not on how '
        + 'many numbers you called.',
  },
  recording: {
    title: 'The call recording',
    body: 'Everything that was heard, from the network’s first signal to the hangup — '
        + 'including what happened before the answer. It loads only when you press play, so '
        + 'that hundreds of files are not pulled down together with the table.',
  },
  reason: {
    title: 'Why this verdict',
    body: 'The explanation of the classification, in words: which signals it relied on and '
        + 'where they contradicted each other. It exists so that any row can be challenged — '
        + 'you look at the same data the platform saw.',
  },
};
